import React, { memo, useState, useCallback } from 'react'
import { Form, Checkbox } from 'antd'
import classnames from 'classnames'
import { extract } from '../../../../../_utils/object'
import { SuggestedFieldProps } from '../types'
import { NotificationSuggestionField } from '../../../../../interfaces/notification-field-suggestions'

/**
 * NoneFoundAddOn component
 *   Re-usable post-label add on
 */
const NoneFoundAddOn = memo(() => <span className="none-found-addon">(No suggestions found)</span>)

/**
 * SuggestedField component (supports children)
 *   Generic field component handles selection and value display.
 *   Value display can be overriden by passing child components
 */
function SuggestedField<F extends NotificationSuggestionField>({
    className,
    label,
    field,
    source,
    form,
    initialValue = false,
    indeterminate = false,
    contentClickable = false,
    onCheckboxChange,
    children,
}: SuggestedFieldProps<F>) {
    const [checked, setChecked] = useState(() => initialValue ?? false)

    const suggestion = extract(source, field)

    // Memoized checkbox change handler
    const handleCheckboxChange = useCallback(
        ({ target: { checked: value } }: { target: { checked: boolean } }) => {
            onCheckboxChange?.(value)
            setChecked(value)
        },
        [onCheckboxChange],
    )

    // Memoized content click handler
    const handleContentClick = useCallback(() => {
        if (!contentClickable) return

        const newValue = !checked
        onCheckboxChange?.(newValue)
        setChecked(newValue)
        form.setFieldsValue({ [field]: newValue })
    }, [contentClickable, checked, onCheckboxChange, form, field])

    return (
        <div className={classnames('suggestion-section', className)}>
            <Form.Item name={field as string} valuePropName="checked" initialValue={initialValue}>
                <Checkbox disabled={!suggestion} indeterminate={indeterminate} onChange={handleCheckboxChange}>
                    {label} {!suggestion ? <NoneFoundAddOn /> : null}
                </Checkbox>
            </Form.Item>
            {suggestion === null ? null : (
                <div
                    className={classnames('field-found-suggestion', {
                        'content-clickable': contentClickable,
                    })}
                    onClick={contentClickable ? handleContentClick : undefined}
                >
                    {children ?? <span>{suggestion}</span>}
                </div>
            )}
        </div>
    )
}

export default memo(SuggestedField) as typeof SuggestedField
